import React, { createContext, useContext, useState } from 'react'

import { GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

export type PlanningShiftTableContextType = {
  selectedStop: GridValidRowModel | null
  setSelectedStop: (row: GridValidRowModel | null) => void
  shiftId: EntityId
  setShiftId: (shiftId: EntityId) => void
  scenarioId: EntityId
  setScenarioId: (scenarioId: EntityId) => void
}

export const PlanningShiftTableContext = createContext<PlanningShiftTableContextType>({
  selectedStop: null,
  setSelectedStop: () => { },
  shiftId: 0,
  setShiftId: () => { },
  scenarioId: 0,
  setScenarioId: () => { },
})

export const usePlanningShiftTableContext = () => {
  const context = useContext(PlanningShiftTableContext)

  if (context === undefined) {
    throw new Error(
      'usePlanningShiftTableContext must be used within a PlanningShiftTableContextProvider',
    )
  }

  return context
}

/**
 * The PlanningShiftTableContextProvider is a context provider that help share the state
 *    between the PlanningShiftTable and the PlanningShiftWindowToolbar components through
 *    the Mosaic Window rendering. Thus, any local actions or state that need to be shared
 *    between these two components should be managed here.
 */
export type PlanningShiftTableContextProps = {
  children?: React.ReactNode
}

export const PlanningShiftTableContextProvider: React.FC<PlanningShiftTableContextProps> = ({ children }) => {
  const [selectedStop, setSelectedStop] = useState<GridValidRowModel | null>(null)
  const [shiftId, setShiftId] = useState<EntityId>(0)
  const [scenarioId, setScenarioId] = useState<EntityId>(0)

  // Memoize the context value to prevent unnecessary re-renders
  const memoContextValue = React.useMemo(() => ({
    selectedStop,
    setSelectedStop,
    shiftId,
    setShiftId,
    scenarioId,
    setScenarioId,
  }), [selectedStop, setSelectedStop, shiftId, setShiftId, scenarioId, setScenarioId])

  return (
    <PlanningShiftTableContext.Provider value={memoContextValue}>
      {children}
    </PlanningShiftTableContext.Provider>
  )
}

